// "เครื่องนี้กำลัง login เป็นร้านไหน" — resolve ให้เร็วที่สุดเท่าที่ทำได้
//
// ทำไมต้องมีไฟล์นี้แยกจาก useShopStore():
//   useShopStore().init() รู้ storeId ก็จริง แต่มันรันอยู่ใน bootstrap queue ซึ่ง
//   ช้าเกินไปสำหรับคนที่ต้องใช้ run mode ตั้งแต่ตอนสร้าง store — pinia plugin
//   อย่าง tableMeta / mobile.items เรียก useRunningMode().getMode() ตอนที่
//   jobs store ถูกสร้าง ซึ่งเกิดใน bootstrap.init() *ก่อน* bootstrap.run()
//   ดังนั้น run mode ที่ผูกกับ storeId ต้องรู้ storeId ตั้งแต่ตอน nuxt plugin
//
// ทุก promise ในนี้ memoize ไว้ boot ครั้งหนึ่งจึงยิง /users/{uid} แค่ครั้งเดียว
// (useShopStore().init() ก็ await ตัวเดียวกัน ไม่ได้ยิงซ้ำ)

import { getAuth, onAuthStateChanged } from "firebase/auth";
import dataservice from "../services/dataservice";
import { getDevStoreOverride, setDevRealStore } from "../utils/devStoreOverride";

/** กันกรณี onAuthStateChanged ไม่ยิง (offline / firebase ค้าง) ไม่ให้ boot ค้างตาม */
const AUTH_TIMEOUT_MS = 8000;

let resolvedStoreId = "";
let uidPromise: Promise<string | null> | null = null;
let userRecordPromise: Promise<any> | null = null;
let userRecordUid = "";
let storeIdPromise: Promise<string> | null = null;

/**
 * storeId ที่ resolve ได้แล้ว แบบ synchronous — "" แปลว่ายังไม่รู้
 * (ยังไม่ login / ยัง resolve ไม่เสร็จ) ผู้เรียกต้องเผื่อเคสนี้เสมอ
 */
export function getResolvedStoreId(): string {
  return resolvedStoreId;
}

/** ให้ useShopStore().init() ป้อน storeId กลับเข้ามา เผื่อ plugin resolve ไม่ทัน/ไม่ได้ */
export function setResolvedStoreId(storeId: string): void {
  const trimmed = storeId?.trim();
  if (trimmed) resolvedStoreId = trimmed;
}

/** uid ของ user ที่ login อยู่ (null = ยังไม่ login) — mirror ลำดับของ authStore.init() */
export function resolveUid(): Promise<string | null> {
  if (uidPromise) return uidPromise;

  uidPromise = new Promise<string | null>((resolve) => {
    // ไม่อ่าน cookie `xtable` ของ beta redirect แล้ว — authStore.init() ใช้ cookie
    // แค่ตรวจว่า firebase session ของ origin นี้เป็น uid เดียวกันไหม ไม่ใช่แทน firebase
    // uid ที่แอปใช้จริงจึงมาจาก firebase auth อย่างเดียว

    let settled = false;
    const done = (uid: string | null) => {
      if (settled) return;
      settled = true;
      resolve(uid);
    };

    const timer = setTimeout(() => done(null), AUTH_TIMEOUT_MS);

    try {
      onAuthStateChanged(
        getAuth(),
        (user) => {
          clearTimeout(timer);
          done(user?.uid ?? null);
        },
        () => {
          clearTimeout(timer);
          done(null);
        }
      );
    } catch {
      clearTimeout(timer);
      done(null);
    }
  });

  return uidPromise;
}

/**
 * GET /users/{uid} แบบ memoize ต่อ uid
 * res.data เป็น array ของ storeId ที่ user คนนี้เข้าถึงได้ ตัวแรกคือร้านของเขา
 */
export function fetchUserRecord(uid: string): Promise<any> {
  if (userRecordPromise && userRecordUid === uid) return userRecordPromise;
  userRecordUid = uid;
  userRecordPromise = dataservice.users.getById(uid);
  return userRecordPromise;
}

/**
 * storeId ที่ boot นี้จะใช้จริง — ลำดับเดียวกับ useShopStore().init():
 *   1. DEV store override (dev เท่านั้น, ถูก tree-shake ทิ้งใน production)
 *   2. storeId ที่ backend map จาก uid
 * คืน "" เมื่อยังไม่ login หรือ resolve ไม่สำเร็จ
 */
export function resolveStoreId(): Promise<string> {
  if (storeIdPromise) return storeIdPromise;

  storeIdPromise = (async () => {
    const uid = await resolveUid();
    if (!uid) return "";

    try {
      const res = await fetchUserRecord(uid);
      const realStoreId = res?.data?.[0] || "";
      if (realStoreId) setDevRealStore(realStoreId);
      resolvedStoreId = getDevStoreOverride() || realStoreId;
      return resolvedStoreId;
    } catch (error) {
      console.warn("[storeIdentity] resolve storeId ไม่สำเร็จ", error);
      return "";
    }
  })();

  return storeIdPromise;
}
